import { useState, useMemo, type MouseEvent } from 'react';
import { DateTime } from 'luxon';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Table } from '@startuptools/ui/Table';
import { Alert } from '@startuptools/ui/Alert';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  useCompleteSccsQuery,
  useShareTransactionGroupsQuery,
  useStgSetSccIdMutation,
} from '../../graphql/react-operations';
import { isNil, uniq } from 'lodash-es';
import { type GqlShareTransactionGroup, GqlTransactionKind } from '../../graphql/base-types.graphql';

export const SelectSccDialog = ({
  stg,
  onClose,
  kind,
}: {
  onClose: (event: MouseEvent<HTMLTableRowElement | HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>) => void;
  stg: Pick<GqlShareTransactionGroup, 'id' | 'kind' | 'transactionDate'>;
  kind?: GqlTransactionKind;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();

  const [mutationError, setMutationError] = useState<unknown | null>(null);
  // eslint-disable-next-line sut/loading-mutation
  const [stgSetSccId] = useStgSetSccIdMutation();

  const {
    data: completeSccsData,
    loading: completeSccsLoading,
    error: completeSccsError,
  } = useCompleteSccsQuery({ variables: { companyId } });

  const {
    data: stgsData,
    loading: stgsLoading,
    error: stgsError,
  } = useShareTransactionGroupsQuery({ variables: { companyId } });

  const dataSource = useMemo(() => {
    if (stgsData && completeSccsData && stg) {
      // const sccs = sccsData.snrShareCapitalChanges.map(
      //   scc =>
      //     ({
      //       ...scc,
      //       sortOrder: scc.sortOrderOverride ?? scc.sortOrderSnr,
      //       splitSharesChange: scc.splitSharesChange,
      //       splitFactor: scc.splitFactor,
      //       decisionDate: scc.decisionDate ? DateTime.fromISO(scc.decisionDate) : undefined,
      //       registrationDate: DateTime.fromISO(scc.registrationDate),
      //     }) satisfies SnrScc,
      // );
      // const stPlusData = stgsToStsPlus(stgsData.shareTransactionGroups);
      // const data = completeSccs(sccs, stPlusData);
      const omittableSccIds = uniq(
        stgsData.shareTransactionGroups.map(e => e.snrShareCapitalChangeId).filter(e => !isNil(e)),
      );
      const relevantSccs = completeSccsData.completeSccs.filter(scc => {
        if (kind === GqlTransactionKind.Split || kind === GqlTransactionKind.Merge) {
          return scc.kind === kind;
        }
        if (isNil(scc.decisionDate) || omittableSccIds.includes(scc.id)) {
          return false;
        }
        if (isNil(stg) || (stg.kind === GqlTransactionKind.Issue && (scc.changedNumberOfShares ?? 0) <= 0)) {
          return false;
        }
        return DateTime.fromISO(stg.transactionDate) >= DateTime.fromISO(scc.decisionDate).minus({ months: 7 });
      });
      return relevantSccs;
    }
    return [];
  }, [stgsData, completeSccsData, stg, kind]);

  const handleRowClick = async (id: string, event: MouseEvent<HTMLTableRowElement>) => {
    if (!id || !stg?.id) {
      return;
    }

    try {
      await stgSetSccId({
        variables: {
          companyId: companyId,
          input: {
            id: stg.id,
            snrShareCapitalChangeId: id,
          },
        },
      });
      onClose(event);
    } catch (error) {
      setMutationError(error);
    }
  };

  const renderDialogContent = () => {
    if (stgsLoading || completeSccsLoading) {
      return null;
    }

    if (stgsError || completeSccsError || mutationError) {
      let errorMessage = (
        <Trans id="An error occurred. Please refresh the page. If the error persists, contact our customer support" />
      );
      if (mutationError) {
        errorMessage = <Trans id="Failed to update data. Please try again." />;
      }

      return (
        <Alert variant="outlined" color="danger">
          {errorMessage}
        </Alert>
      );
    }

    if (dataSource.length === 0) {
      return (
        <Alert variant="outlined" color="neutral" sx={{ minWidth: '400px' }}>
          <Trans id="No entries so far." />
        </Alert>
      );
    }

    return (
      <Table hoverRow sx={{ tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th>
              <Trans id="Name" />
            </th>
            <th>
              <Trans id="Decision Date" />
            </th>
            <th>
              <Trans id="Registration Date" />
            </th>
            <th align="right">
              <Trans id="Shares Change" />
            </th>
            <th align="right">
              <Trans id="Filing Number" />
            </th>
          </tr>
        </thead>
        <tbody>
          {dataSource.map(row => (
            <tr key={row.id} onClick={event => handleRowClick(row.sccId, event)} style={{ cursor: 'pointer' }}>
              <td>{row.name}</td>
              <td>{row.decisionDate ? <DateFormat locale={i18n.locale}>{row.decisionDate}</DateFormat> : ''}</td>
              <td>
                {row.registrationDate ? <DateFormat locale={i18n.locale}>{row.registrationDate}</DateFormat> : ''}
              </td>
              <td align="right">
                <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                  {row.changedNumberOfShares}
                </NumberFormat>
              </td>
              <td align="right">
                {row.filingNumber}/{row.filingYear}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Modal
      open
      onClick={ev => {
        ev.stopPropagation();
        ev.preventDefault();
      }}
      onClose={onClose}
    >
      <ModalDialog>
        <ModalClose />
        <Stack gap={3}>
          <Typography level="h4">
            {kind === GqlTransactionKind.Split || kind === GqlTransactionKind.Merge ? (
              <Trans id="Connect Split " />
            ) : (
              <Trans id="Connect Share Issue" />
            )}
          </Typography>
          {renderDialogContent()}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
